/**
 * TeamSystem Impact - Reporting Years API
 * Elenco unificato degli anni di rendicontazione e gestione del loro ciclo
 * di vita (archiviazione / ripristino / eliminazione definitiva).
 */

import { apiClient } from './client';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ManagedYear {
  year: number;
  archived: boolean;
  archivedAt?: string;
}

export interface DeletionBreakdownItem {
  key: string;
  label: string;
  count: number;
}

export interface DeletionPreview {
  year: number;
  total: number;
  breakdown: DeletionBreakdownItem[];
}

function unwrap<T>(response: { data: ApiResponse<T> }, fallbackMsg: string): T {
  if (!response.data.success) {
    throw new Error(response.data.error || fallbackMsg);
  }
  return response.data.data as T;
}

export const reportingYearsApi = {
  /** Anni attivi (esclude gli archiviati) — usato dal selettore globale. */
  list: async (): Promise<number[]> => {
    const response = await apiClient.get<ApiResponse<number[]>>('/reporting-years');
    return unwrap(response, 'Impossibile caricare gli anni') ?? [];
  },

  /** Elenco completo con stato — pagina Gestione Anno (admin). */
  listManaged: async (): Promise<ManagedYear[]> => {
    const response = await apiClient.get<ApiResponse<ManagedYear[]>>('/reporting-years/managed');
    return unwrap(response, 'Impossibile caricare gli anni') ?? [];
  },

  /** Archivia un anno (livello 1, reversibile). */
  archive: async (year: number): Promise<ManagedYear> => {
    const response = await apiClient.post<ApiResponse<ManagedYear>>(`/reporting-years/${year}/archive`);
    return unwrap(response, "Impossibile archiviare l'anno");
  },

  /** Ripristina un anno archiviato. */
  restore: async (year: number): Promise<ManagedYear> => {
    const response = await apiClient.post<ApiResponse<ManagedYear>>(`/reporting-years/${year}/restore`);
    return unwrap(response, "Impossibile ripristinare l'anno");
  },

  /** Anteprima conteggi di cosa verrebbe eliminato. */
  deletionPreview: async (year: number): Promise<DeletionPreview> => {
    const response = await apiClient.get<ApiResponse<DeletionPreview>>(`/reporting-years/${year}/deletion-preview`);
    return unwrap(response, "Impossibile calcolare l'anteprima");
  },

  /** Eliminazione DEFINITIVA (livello 2, solo se archiviato). */
  remove: async (year: number): Promise<DeletionPreview> => {
    const response = await apiClient.delete<ApiResponse<DeletionPreview>>(`/reporting-years/${year}`);
    return unwrap(response, "Impossibile eliminare l'anno");
  },
};
